import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as yup from 'yup';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Input from '@mui/material/Input';
import Dialog from '@mui/material/Dialog';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { FormInputText } from 'components/Common/Forms/FormTextField';
import { displaySnackbar } from 'components/snackbar/store/SnackbarSlice';
import { postStagedDocumentUpload } from 'layouts/documentManagement/services/documentManagementApi';
import {
  GridForm,
  ButtonIcon,
  UploadBox,
  UploadText,
  RoundedCloudUpload,
  ButtonSpinnerProgress,
} from 'components/Ui/styled';
import {
  renderSaveCancelButtons,
  useNotification,
  getDialogHeading,
  fieldValidation,
  getSnackBar,
  refinePayload,
} from 'utils/commonUtils';
import { PaperPropsCheck, characterLimit } from 'constants';

const stagedDocumentUploadSchema = yup.object().shape({
  filename: fieldValidation(characterLimit),
});

const StagedDocumentUpload = (props) => {
  const { open, setOpen, fetchStagedEmployeeDocuments } = props;
  const dispatch = useDispatch();
  const { setOpenNotification, setNotificationMessage, NotificationPopup } =
    useNotification();
  const [filename, setFilename] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUploadUrl, setFileUploadUrl] = useState(null);
  const [isUploadAccess, setIsUploadAccess] = useState(false);
  const [uploadAccessError, setUploadAccessError] = useState(null);
  const [loadingButton, setLoadingButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const employee = useSelector((state) => state.employee?.employee);

  const defaultValue = {
    filename: filename,
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
    resolver: yupResolver(stagedDocumentUploadSchema),
  });

  const { handleSubmit, reset, control, register, formState } = useFunction;
  const { errors } = formState;

  useEffect(() => {
    if (selectedFile) {
      fileUpload();
    }
  }, [selectedFile]);

  const fileUpload = async () => {
    setLoadingButton(true);
    const payload = {
      filename,
    };
    try {
      const stagedDocumentUploadUrl = await postStagedDocumentUpload(
        employee,
        refinePayload(payload)
      );
      setFileUploadUrl(stagedDocumentUploadUrl?.link);
      setLoadingButton(false);
    } catch (error) {
      setIsUploadAccess(true);
      setLoadingButton(false);
      setUploadAccessError(error.response.data.message);
    }
  };

  useEffect(() => {
    reset(defaultValue);
  }, [defaultValue.filename]);

  const handleClose = () => {
    setSelectedFile(null);
    setFilename(null);
    setFileUploadUrl(null);
    setIsUploadAccess(false);
    setUploadAccessError(null);
    reset(defaultValue);
    setOpen(false);
  };

  const changeHandler = (event) => {
    const selected = event.target.files[0];
    if (!selected) {
      return;
    }
    setSelectedFile(selected);
    setFilename(event.target.files[0]?.name);
  };

  const onSubmit = async () => {
    setIsLoading(true);
    const snackBar = getSnackBar('File uploaded successfully');
    if (isUploadAccess) {
      setOpenNotification(true);
      setNotificationMessage(uploadAccessError);
    } else {
      try {
        await axios.put(fileUploadUrl, selectedFile, {
          headers: {
            'Content-Type': '',
          },
        });
        handleClose();
        dispatch(displaySnackbar(snackBar));
        fetchStagedEmployeeDocuments();
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
    setIsLoading(false);
  };

  return (
    <>
      <Dialog open={open} PaperProps={PaperPropsCheck}>
        {getDialogHeading('Document Upload', handleClose)}
        <GridForm container spacing={2}>
          <Grid item xs={12}>
            <FormInputText
              name="filename"
              type="text"
              control={control}
              label="File Name"
              helperText={errors?.filename?.message}
              errors={!!errors.filename}
              disabled={true}
            />
          </Grid>
        </GridForm>
        <Box mt={1}></Box>
        <GridForm container spacing={2}>
          <Grid item xs={12}>
            <UploadBox>
              <ButtonIcon style={{ padding: '0px' }}>
                <label
                  htmlFor="contained-button-file"
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <UploadText variant="h6">Choose file to upload</UploadText>
                  <RoundedCloudUpload fontSize="large" />
                  <Input
                    //accept='image/*'
                    id="contained-button-file"
                    type="file"
                    style={{ display: 'none' }}
                    onChange={changeHandler}
                  />
                </label>
              </ButtonIcon>
              {loadingButton && <ButtonSpinnerProgress size={24} />}
            </UploadBox>
          </Grid>
          {renderSaveCancelButtons(
            handleClose,
            handleSubmit(onSubmit),
            isLoading
          )}
        </GridForm>
      </Dialog>
      <NotificationPopup />
    </>
  );
};

export default StagedDocumentUpload;
